import { faChevronRight, faCloudDownload, faExternalLink } from '@fortawesome/pro-light-svg-icons';
import { faFolder, faFolderOpen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { DataNode } from 'rc-tree/lib/interface';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DirectoryTree, Tooltip, useExistingTranslation } from 'src/common';
import { getIconByFileType } from 'src/helpers/fileTypeIcon';
import { DateFormatTranslation } from 'src/lib/I18nService/types';
import { useDownloadFreeFile, useTenderFree } from 'src/models/procurements/TenderFree/hooks';
import { IProcurementFile } from 'src/models/procurements/Tender/types';
import styles from './index.module.scss';
import { TenderSubCat } from 'src/shared';

type ToChildrenFileFunction = (
  file: IProcurementFile,
  fileIndex: number,
  indexConcat: string,
  getFile: (path: string) => void
) => ILeafDataNode;

const useChildrenFile = (): ToChildrenFileFunction => {
  const { t } = useTranslation();

  const toChildrenFile: ToChildrenFileFunction = (file, fileIndex, indexConcat, getFile) => {
    const isFile = !file.children || file.children.length === 0;
    const title = file.directoryName ?? file.fileName;
    const icon = getIconByFileType(file.fileType);

    return {
      title: isFile ? (
        <>
          <Button
            type={'text'}
            className={styles.docButton}
            onClick={e => {
              e.stopPropagation();
              file.targetPath && getFile(file.targetPath);
            }}
            icon={<FontAwesomeIcon icon={faCloudDownload} className={styles.downloadIcon} />}
          >
            <span className={styles.docTitle}>{title}</span>
          </Button>
          <span className={styles.docDate}>{t(DateFormatTranslation.full, { date: file.dateUploaded })}</span>
        </>
      ) : (
        <span className={styles.folderTitle}>{`${title} (${file.children?.length})`}</span>
      ),
      isLeaf: isFile,
      path: file.targetPath,
      fileType: file.fileType,
      key: `${indexConcat}-${fileIndex}`,
      switcherIcon: ({ expanded }) =>
        !isFile && (
          <FontAwesomeIcon
            icon={faChevronRight}
            className={classNames(styles.expandIcon, { [styles.isExpanded]: expanded })}
          />
        ),
      icon: ({ expanded }) => (isFile ? <>{icon}</> : <FontAwesomeIcon icon={expanded ? faFolderOpen : faFolder} />),
      children: file.children?.map((fileNode, index) =>
        toChildrenFile(fileNode, index, `${indexConcat}-${fileIndex}`, getFile)
      )
    } as ILeafDataNode;
  };

  return useCallback(toChildrenFile, [t, toChildrenFile]);
};

interface ILeafDataNode extends DataNode {
  path: string;
  fileType: string;
}

export const DocsSubCat: FC = () => {
  const { data } = useTenderFree();
  const docsCat = data?.docsCat;
  const filesCats = useMemo(() => docsCat?.fileCategories ?? [], [docsCat?.fileCategories]);
  const filesQty = useMemo(() => docsCat?.filesAmount ?? 0, [docsCat?.filesAmount]);
  const linkToDocsSource = useMemo(() => docsCat?.linkToDocsSource, [docsCat?.linkToDocsSource]);
  const { t } = useTranslation();
  const { existingT: filesCatT } = useExistingTranslation();
  const { getFile, loading } = useDownloadFreeFile();
  const toChildrenFile = useChildrenFile();

  const treeData: DataNode[] = useMemo(
    () =>
      filesCats.map((fileCategory, index) => {
        return {
          key: `${index}`,
          title: (
            <Tooltip title={filesCatT(`Tenders.FileCategory.${fileCategory.description}`)}>
              <span className={styles.folderTitle}>{`${filesCatT(`Tenders.FileCategory.${fileCategory.name}`)} (${
                fileCategory.children.length
              })`}</span>
            </Tooltip>
          ),
          icon: ({ expanded }) => <FontAwesomeIcon icon={expanded ? faFolderOpen : faFolder} />,
          switcherIcon: ({ expanded }) => (
            <FontAwesomeIcon
              icon={faChevronRight}
              className={classNames(styles.expandIcon, { [styles.isExpanded]: expanded })}
            />
          ),
          children: fileCategory.children.map((file, fileIndex) =>
            toChildrenFile(file, fileIndex, `${index}-${fileIndex}`, getFile)
          )
        };
      }),
    [filesCatT, filesCats, getFile, toChildrenFile]
  );

  return docsCat ? (
    <TenderSubCat subCat={docsCat} boxTheme={false}>
      <div className={styles.content}>
        {!!filesQty && treeData ? (
          <>
            <DirectoryTree
              defaultExpandedKeys={Object.keys(treeData)}
              className={classNames(styles.directoryTree, loading ? styles.loading : null)}
              selectable={false}
              treeData={treeData}
            />
            <span className={styles.warningText}>{t('DetailsPage.docsWarning')}</span>
          </>
        ) : (
          <span className={styles.emptyText}>{t('DetailsPage.noDocsAvailable')}</span>
        )}
        {linkToDocsSource && (
          <div className={styles.filesLink}>
            <a className={styles.tenderLink} rel="noopener noreferrer" target="_blank" href={linkToDocsSource}>
              <FontAwesomeIcon icon={faExternalLink} className={styles.tenderLinkIcon} />
              <span>{t('DetailsPage.linkToDocsSource')}</span>
            </a>
          </div>
        )}
      </div>
    </TenderSubCat>
  ) : null;
};

export default DocsSubCat;
